<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, string } from 'yup'
import { useI18n } from 'vue-i18n'
import {
  DCard,
  DBtn,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DEmployeesFilter,
  DLoading,
  FilterLinesIcon,
  Users01Icon,
  DTextField
} from 'front-library'
import {
  convertArrayToString,
  getPayroll,
  getProcessCountry,
  getConcepts,
  getReportRequest,
  getEmployeesReport,
  getListYear
} from '@/app/core/services/reports'
import {
  ReportINS,
  generateINS,
  getDataCompany
} from '@/app/core/services/reports/banks'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsConcepts = ref<any[]>([])
const allConcepts = ref<any[]>([])
const allIdsConceptsSelected = ref<any[]>([])
const itemsEmployees = ref<string[]>([])
const itemsProcesses = ref<string[]>([])
const itemsIdentificationFormat = ref([
  { id: 'CEDULA', name: 'Cedula' },
  { id: 'FISCAL-ID', name: 'Fiscal ID' }
])
const itemsMonths = ref([
  { id: 'january', name: t('reports.months.january') },
  { id: 'february', name: t('reports.months.february') },
  { id: 'march', name: t('reports.months.march') },
  { id: 'april', name: t('reports.months.april') },
  { id: 'may', name: t('reports.months.may') },
  { id: 'june', name: t('reports.months.june') },
  { id: 'july', name: t('reports.months.july') },
  { id: 'august', name: t('reports.months.august') },
  { id: 'september', name: t('reports.months.september') },
  { id: 'october', name: t('reports.months.october') },
  { id: 'november', name: t('reports.months.november') },
  { id: 'december', name: t('reports.months.december') }
])
const itemsYears = ref<any[]>([])
const searchIdentificationFormat = ref<string>('')
const searchMonths = ref<string>('')
const urlFile = ref(null)
const refresh = ref()
const path = route.params.code as string
const decodeCode = atob(path)

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  processes: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  conceptsAmount: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  conceptsQuantityHours: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  month: string().required(t('form.valid.required')),
  year: string().required(t('form.valid.required')),
  modifiers: object({
    identificationFormat: string().required(t('form.valid.required')),
    policyNumber: string().required(t('form.valid.required'))
  }),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') }))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: processes } = useField<string[]>(ref('processes'))
const { value: conceptsAmount } = useField<string[]>(ref('conceptsAmount'))
const { value: conceptsQuantityHours } = useField<string[]>(
  ref('conceptsQuantityHours')
)
const { value: conceptsSchoolSalary } = useField<string[]>(
  ref('modifiers.conceptsSchoolSalary')
)
const { value: month } = useField<string>('month')
const { value: year } = useField<string>('year')
const { value: identificationFormat } = useField<string>(
  ref('modifiers.identificationFormat')
)
const { value: policyNumber } = useField<string>(ref('modifiers.policyNumber'))
const { value: format } = useField(ref('format'))
const { value: employees } = useField<any[]>(ref('employees'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  values.conceptsAmount = filterCodeConcepts(conceptsAmount.value)
  values.conceptsQuantityHours = filterCodeConcepts(conceptsQuantityHours.value)
  values.modifiers.conceptsSchoolSalary = filterCodeConcepts(
    conceptsSchoolSalary.value
  )
  const createReport: ReportINS = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateINS(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null
  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar empleados */
const getListEmployees = async () => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (payrolls.value.length !== 0) {
      const strPayrolls = await convertArrayToString(payrolls.value)
      itemsEmployees.value = await getEmployeesReport(strPayrolls, '', '')
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Listar conceptos */
const listConcepts = async () => {
  loading.value = true
  messageLoading.value = t('loading.concepts')
  itemsConcepts.value = []
  const strProcesses = await convertArrayToString(processes.value)
  const strPayrolls = await convertArrayToString(payrolls.value)
  try {
    if (payrolls.value?.length > 0) {
      allConcepts.value = await getConcepts(
        strPayrolls,
        'INS',
        '',
        strProcesses
      )
      if (conceptsAmount.value?.length > 0)
        filterCodeConcepts(conceptsAmount.value)
      if (conceptsQuantityHours.value?.length > 0)
        filterCodeConcepts(conceptsQuantityHours.value)
      if (conceptsSchoolSalary.value?.length > 0)
        filterCodeConcepts(conceptsSchoolSalary.value)
      itemsConcepts.value = [
        ...new Map(
          allConcepts.value.map((item: any) => [item['code'], item])
        ).values()
      ]
      conceptsAmount.value = filterChangeProcesses(conceptsAmount.value) ?? []
      conceptsQuantityHours.value =
        filterChangeProcesses(conceptsQuantityHours.value) ?? []
      conceptsSchoolSalary.value =
        filterChangeProcesses(conceptsSchoolSalary.value) ?? []
      if (itemsConcepts.value.length === 0) {
        notifications.addNotification({
          title: t('reports.notification.noConceptsFound'),
          text: t('reports.notification.noConcepts'),
          type: 'warning',
          time: 10000
        })
      }
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Filtrar procesos */
const filterChangeProcesses = (concepts: string[]) => {
  if (concepts) {
    const conceptsSelected = itemsConcepts.value
      .filter((_: any) => concepts.indexOf(_.code) > -1)
      .map((_: any) => _.code)
    return conceptsSelected
  }
}

/* Filtrar codigos de conceptos */
const filterCodeConcepts = (concepts: any) => {
  allIdsConceptsSelected.value = allConcepts.value
  allIdsConceptsSelected.value = allIdsConceptsSelected.value
    .filter(item => concepts.indexOf(item.code) > -1)
    .map((_: any) => _.id)
  return allIdsConceptsSelected.value
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const [promisePayrolls, promiseProcess, promiseCompanyData] =
      await Promise.all([
        getPayroll(store.user?.id as string),
        getProcessCountry(),
        getDataCompany(storePayroll.payroll?.company.id as string)
      ])
    itemsPayrolls.value = promisePayrolls
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    itemsProcesses.value = promiseProcess
    policyNumber.value = promiseCompanyData.policy
    identificationFormat.value = 'CEDULA'
    itemsYears.value = getListYear()
    await getListEmployees()
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.ins')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="getListEmployees(), listConcepts()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.processes')"
                    v-model="processes"
                    :header-title="t('reports.fields.processes')"
                    :required="true"
                    :options="itemsProcesses"
                    :error="errors.processes"
                    @change="listConcepts()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.salary')"
                    v-model="conceptsAmount"
                    :header-title="t('reports.fields.salary')"
                    :value-key="'code'"
                    :options="itemsConcepts"
                    :required="true"
                    :error="errors.conceptsAmount" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.numberHours')"
                    v-model="conceptsQuantityHours"
                    :header-title="t('reports.fields.numberHours')"
                    :value-key="'code'"
                    :options="itemsConcepts"
                    :required="true"
                    :error="errors.conceptsQuantityHours" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.month')"
                    v-model="month"
                    :required="true"
                    :options="filterOptions(itemsMonths, searchMonths)"
                    :error="errors.month"
                    @filter="searchMonths = $event" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.year')"
                    v-model="year"
                    :required="true"
                    :options="itemsYears"
                    :error="errors.year" />
                </div>
                <hr class="col-span-6" />
                <h1
                  class="col-span-6 text-gray-600"
                  v-html="t('reports.tabs.modifiers')" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :required="true"
                    :label="t('reports.fields.policy')"
                    v-model="policyNumber"
                    :error="errors['modifiers.policyNumber']" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.schoolSalary')"
                    v-model="conceptsSchoolSalary"
                    :header-title="t('reports.fields.schoolSalary')"
                    :value-key="'code'"
                    :options="itemsConcepts" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.identificationFormat')"
                    v-model="identificationFormat"
                    :required="true"
                    :options="
                      filterOptions(
                        itemsIdentificationFormat,
                        searchIdentificationFormat
                      )
                    "
                    :error="errors['modifiers.identificationFormat']"
                    @filter="searchIdentificationFormat = $event" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3
                  class="text-md font-medium leading-6 text-gray-900"
                  v-html="t('fields.employees')" />
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="
                  onSubmit(),
                    (format =
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
                "
                v-html="t('form.button.excel')" />
              <DBtn
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit(), (format = 'text/plain')"
                v-html="t('form.button.generateFile')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
